import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { ICompany, defaultValue } from 'app/shared/model/company.model';
import { CompanyType } from 'app/shared/model/enumerations/company-type.model';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<ICompany> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/companies';

// Actions

interface IPagedCompanyQueryParams extends IQueryParams {
  nameSearch?: string;
  status?: StatusFilter;
}

interface IRequestCompany {
  company: ICompany;
  representativeCompanies: number[];
  representativeTruckImplements: number[];
  defaultAlertIds: number[];
}

export const getCompanyDataSummary = createAsyncThunk(
  'company/fetch_entity_list',
  async ({ page, size, sort, nameSearch, status }: IPagedCompanyQueryParams) => {
    const requestUrl = `${apiUrl}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&search=${nameSearch}&status=${status}&` : ''
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<ICompany[]>(requestUrl);
  }
);

export const getEntities = createAsyncThunk('company/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ICompany[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'company/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICompany>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'company/create_entity',
  async (entity: IRequestCompany) => {
    const result = await axios.post<any>(`${apiUrl}/new`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'company/update_entity',
  async (entity: IRequestCompany) => {
    const result = await axios.put<ICompany>(`${apiUrl}/new/${entity.company.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'company/partial_update_entity',
  async (entity: ICompany) => {
    const result = await axios.patch<ICompany>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'company/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ICompany>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getLoggedCompany = createAsyncThunk(
  'company/logged_company',
  async () => {
    const requestUrl = `${apiUrl}/logged-company`;
    return axios.get<ICompany>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

interface IGetAllCompaniesByType {
  type: CompanyType;
}

export const getAllCompaniesByType = createAsyncThunk('company/fetch_entity_list', async ({ type }: IGetAllCompaniesByType) => {
  const requestUrl = `${apiUrl}/type/${type}`;
  return axios.get<ICompany[]>(requestUrl);
});

// slice

export const CompanySlice = createEntitySlice({
  name: 'company',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getLoggedCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getAllCompaniesByType), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getAllCompaniesByType, getEntity, getLoggedCompany), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CompanySlice.actions;

// Reducer
export default CompanySlice.reducer;
